// InteliChat adapter: native conversation state (read-only + fetch trigger).
// Wraps the Vuex conversation + stats getters so InteliChat workspaces
// (Today, Inbox triage) read native truth without touching Vuex paths.
// NOTE: counts populate once the native stats/list fetch has run; this
// adapter never fabricates values - absent data reads as null.
import { computed, onUnmounted } from 'vue';
import { createConversationListRecovery } from 'dashboard/attention/conversationListRecovery';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import { attentionEnabled, attentionFor, attentionState } from 'dashboard/attention/state';
import { conversationTimestamp, firstReplyState } from './conversationTimestamps';

export function useInteliConversation() {
  const store = useStore();
  const currentUser = useMapGetter('getCurrentUser');
  const currentAttention = () => {
    const account = Number(window.location.pathname.match(/\/app\/accounts\/(\d+)\//)?.[1]);
    const snapshot = attentionState.snapshot;
    return account > 0 && snapshot?.account_id === account && snapshot.actor_id === currentUser.value?.id ? snapshot : null;
  };
  const durableRow = chat => currentAttention() ? attentionFor(chat?.id) : undefined;

  const stats = useMapGetter('conversationStats/getStats');
  const allConversations = useMapGetter('getAllConversations');
  const listLoadingStatus = useMapGetter('getChatListLoadingStatus');

  const mineCount = computed(() => {
    const count = stats.value?.mineCount;
    return typeof count === 'number' && count > 0 ? count : null;
  });

  const unassignedCount = computed(() => {
    const count = stats.value?.unAssignedCount;
    return typeof count === 'number' && count > 0 ? count : null;
  });

  const allCount = computed(() => {
    const count = stats.value?.allCount;
    return typeof count === 'number' && count > 0 ? count : null;
  });

  // The account's OPEN conversations as fetched by the native store
  // (the same list the native inbox renders - real data, most recent
  // activity first; a page at a time, exactly like the native list).
  // Conversations the never-miss HUD actively WATCHES: OPEN or PENDING. A
  // pending customer who is waiting still needs alerting just like an open
  // one (Tyler 2026-07-13), so pending is included here and loaded on mount
  // via ensureConversationsLoaded. (Name kept for its consumers.)
  const openConversations = computed(() =>
    (allConversations.value || []).filter(
      chat => chat.status === 'open' || chat.status === 'pending'
    )
  );

  // Trigger the native fetch machinery (same dispatches ChatList.vue
  // makes): open conversations across the account, newest activity
  // first, plus the real mine/unassigned/all tab counts from /meta.
  const conversationFetchParams = {
    assigneeType: 'all',
    status: 'open',
    sortBy: 'last_activity_at_desc',
    page: 1,
  };

  // The custom workspaces replace ChatList, so retain its native metadata
  // subscription for assignment, creation and status updates. The upstream
  // action coalesces bursts; useEmitter removes this listener on unmount.
  useEmitter('fetch_conversation_stats', () => {
    // Native ChatList owns inbox/team/status filters on native routes.
    if (/\/app\/accounts\/\d+\/intelichat\/(today|inbox)(?:\/|$)/.test(window.location.pathname)) {
      store.dispatch('conversationStats/get', conversationFetchParams);
    }
  });

  const nativeListRecovery = createConversationListRecovery(store, () =>
    /\/app\/accounts\/\d+\/intelichat\/(today|inbox)(?:\/|$)/.test(window.location.pathname)
      ? { params: conversationFetchParams, pages: Math.max(1, store.getters['conversationPage/getCurrentPageFilter']?.('all') || 1), filter: 'all', extraParams: [{ ...conversationFetchParams, status: 'pending' }] }
      : null);
  useEmitter('reconcile_conversation_list', revision => nativeListRecovery.offer(revision));
  onUnmounted(() => nativeListRecovery.dispose());

  // Load PENDING conversations into the shared store WITHOUT disturbing the
  // agent's open-tab list filter or its stats: a direct read merged per-record
  // via updateConversation (a clean upsert). The agent's status-scoped list
  // does not surface these; only the never-miss buckets pick them up, and
  // real-time updates to them then flow through the store as usual.
  const loadPendingConversations = async () => {
    try {
      const response = await ConversationAPI.get({
        assigneeType: 'all',
        status: 'pending',
        sortBy: 'last_activity_at_desc',
        page: 1,
      });
      const list = response?.data?.data?.payload || [];
      list.forEach(chat => store.dispatch('updateConversation', chat));
    } catch (e) {
      // absent data reads as empty - never fabricate
    }
  };

  const ensureConversationsLoaded = async () => {
    store.dispatch('updateChatListFilters', conversationFetchParams);
    store.dispatch('conversationStats/get', conversationFetchParams);
    await store.dispatch('fetchAllConversations');
    await loadPendingConversations();
  };

  // ---- per-conversation derivations (all from native fields) ----
  const contactName = chat =>
    chat?.meta?.sender?.name || chat?.meta?.sender?.email || 'Customer';

  const assigneeName = chat =>
    chat?.meta?.assignee?.available_name || chat?.meta?.assignee?.name || '';

  const isUnassigned = chat => !chat?.meta?.assignee;

  // Active attention uses the durable clock, independent of upstream bot or
  // automated replies. Missing snapshot evidence cannot invent a waiting age.
  const waitingSince = chat => {
    if (!attentionEnabled()) return conversationTimestamp(chat?.waiting_since);
    const row = durableRow(chat);
    return row?.waiting === true ? conversationTimestamp(row.waiting_since) : 0;
  };

  const isAwaitingReply = chat => waitingSince(chat) > 0;

  const isUnattended = chat => {
    if (!attentionEnabled()) return firstReplyState(chat) === 'none';
    const row = durableRow(chat);
    return row?.waiting === true && row.human_response_recorded === false;
  };

  const attentionStatus = chat => {
    if (!attentionEnabled()) return 'legacy';
    const row = durableRow(chat);
    if (!row) return 'unknown';
    if (row.state === 'deferred') return 'deferred';
    if (row.waiting === true) return 'awaiting_reply';
    if (row.human_response_recorded == null) return 'unknown';
    return row.waiting === false ? 'handled' : 'unknown';
  };

  const lastActivityAt = chat =>
    Number(chat?.last_activity_at || chat?.timestamp || 0);

  const lastMessage = chat => chat?.last_non_activity_message || null;

  return {
    stats,
    mineCount,
    unassignedCount,
    allCount,
    allConversations,
    openConversations,
    listLoadingStatus,
    ensureConversationsLoaded,
    contactName,
    assigneeName,
    isUnassigned,
    waitingSince,
    isAwaitingReply,
    isUnattended,
    attentionStatus,
    currentAttention,
    lastActivityAt,
    lastMessage,
  };
}
